import { useContext } from "react";
import config from "../../config";
import { useQuery } from "@tanstack/react-query";
import { AppContext } from "@/App.context";
import { SearchContext } from "@/page-components/search-module/search-context";
import {
  API_LOADING_ERROR,
  API_NO_DATA_FOUND_ERROR,
  API_NO_DATA_FOUND_FOR_DATE_RANGE_ERROR,
  API_NO_DATA_FOUND_FOR_FILTERS_ERROR,
  API_NOT_FOUND_ERROR,
  ERROR_STATUS_CODES,
} from "@/lib/errors/error-constants";

/**
 * @typedef GetAllStatistics
 * @property {getSpendings} spendings
 */

/**
 * @typedef getSpendings
 * @property {string} title
 * @property {string} cost
 */

/**
 * @typedef GetAllStatisticsRes
 * @property {GetAllStatistics} data
 */

/**
 * @typedef GetAllStatisticsReq
 * @property {'aws' | 'gcp' | 'azure' | 'allclouds' } service
 * @property {string} fromDate
 * @property {string} toDate
 * @property {string} type
 * @property {string} name
 * @property {string} accounts
 * @property {string} regions
 * @property {string} spendingBy
 * @property {string} spendingFilterService
 * @property {string} spendingFilterTag
 */

const fetchSpendBySearch = (token, searchContext) => {
  /**
   * @param {GetAllStatisticsReq} req
   * @param {AbortSignal=} signal
   * @returns {Promise<GetAllStatisticsRes>}
   */
  return async (
    {
      service,
      fromDate,
      toDate,
      accounts,
      regions,
      type,
      name,
      spendingBy,
      spendingFilterService,
      spendingFilterTag,
    },
    signal
  ) => {
    const body = {
      accounts: accounts || [],
      regions: regions || [],
      fromDate: fromDate,
      toDate: toDate,
      type: type,
      name: name,
      spendingBy: spendingBy,
    };

    if (searchContext?.type === "serviceCategory") {
      body.spendingFilterService = spendingFilterService;
    }
    if (
      // searchContext?.type === "serviceCategory" &&
      searchContext?.filterSelectedValue === "Tags"
    ) {
      body.spendingFilterTag = spendingFilterTag;
    }

    const statistics = await fetch(
      `${config.apiHost}/v1/${service}/search/search-result/spend-by`,
      {
        method: "POST",
        body: JSON.stringify(body),
        signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      }
    );

    if (!statistics.ok) {
      if (ERROR_STATUS_CODES.includes(statistics.status)) {
        throw new Error(API_LOADING_ERROR);
      }
      if (statistics.status === 404) {
        throw new Error(API_NOT_FOUND_ERROR);
      }
      throw new Error((await statistics.json()).message);
    }
    const response = await statistics.json();

    if (
      response?.spendings.length === 0 ||
      (response?.spendings.length === 1 &&
        response.spendings[0].title === null &&
        response.spendings[0].cost === 0)
    ) {
      if (
        (searchContext?.type === "serviceCategory" &&
          body.spendingFilterService) ||
        (searchContext?.type === "serviceCategory" &&
          searchContext?.filterSelectedValue === "Tags" &&
          body.spendingFilterTag)
      ) {
        throw new Error(API_NO_DATA_FOUND_FOR_FILTERS_ERROR);
      }
      if (fromDate && toDate) {
        throw new Error(API_NO_DATA_FOUND_FOR_DATE_RANGE_ERROR);
      }
      if (
        body.accounts.length ||
        body.regions.length ||
        body.spendingBy ||
        body.name ||
        body.type
      ) {
        throw new Error(API_NO_DATA_FOUND_FOR_FILTERS_ERROR);
      } else {
        throw new Error(API_NO_DATA_FOUND_ERROR);
      }
    }
    return response;
  };
};
/**
 * @param {GetAllStatisticsReq} req
 * @param {any[]=} deps
 */
export const useSpendBySearch = (req, deps = []) => {
  const appContext = useContext(AppContext);
  const searchContext = useContext(SearchContext);

  const query = useQuery({
    queryKey: ["getSpendBySearch", req, ...deps],
    queryFn: ({ signal }) =>
      fetchSpendBySearch(appContext.idToken, searchContext)(req, signal),
  });

  return query;
};
